/**
 * ReferenceCheckModal — confirm modal for landlord-triggered reference checks (DC-17).
 *
 * Collects the referee list ({email, relationship, context?}) and the information
 * package (tenantNames, landlordName, landlordContact, requestedInformation), then
 * calls triggerReferenceCheck (POST .../reference-checks — BR-13, gated server-side
 * by BR-9 rental-history consent). Mirrors the RejectionNoticeModal state-machine +
 * focus-trap pattern used elsewhere in this directory.
 *
 * This modal never displays referee reply content — it only collects the outbound
 * request; the read-only status view (VerificationsTab) is what later renders
 * referee statuses, never replies (BR-10/BR-12).
 */

import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ReferenceCheckConflictCodeSchema } from '@resida/api-types/leasing';
import { Button } from '../../../../components/ui/button';
import { ApiClientError } from '../../../../lib/api';
import {
  triggerReferenceCheck,
  type ReferenceCheckRefereeInput,
} from '../../../../lib/leasing.api';
import { REFERENCE_AGENT_SETTINGS_PATH } from '../../../../lib/reference-check-links';

/**
 * BR-28 — the reference agent was switched off after the tab loaded (the server gate is
 * authoritative). Sending again cannot succeed, so this refusal has its own step: a route to the
 * switch instead of "Try again", and a plain note that leaving does not keep what was typed.
 */
const REFERENCE_AGENT_DISABLED_MESSAGE = 'The reference agent is off, so this reference check was not sent.';

type ModalState =
  | { step: 'idle' }
  | { step: 'submitting' }
  | { step: 'success'; referenceCheckId: string }
  | { step: 'error'; message: string; agentOff: boolean };

interface RefereeDraft {
  email: string;
  relationship: string;
  context: string;
}

function emptyReferee(): RefereeDraft {
  return { email: '', relationship: '', context: '' };
}

export interface ReferenceCheckModalProps {
  leadId: string;
  applicantId: string;
  applicantName: string;
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (referenceCheckId: string) => void;
}

export function ReferenceCheckModal({
  leadId,
  applicantId,
  applicantName,
  isOpen,
  onClose,
  onCreated,
}: ReferenceCheckModalProps) {
  const [tenantNames, setTenantNames] = useState('');
  const [landlordName, setLandlordName] = useState('');
  const [landlordContact, setLandlordContact] = useState('');
  const [requestedInformation, setRequestedInformation] = useState('');
  const [referees, setReferees] = useState<RefereeDraft[]>([emptyReferee()]);
  const [modalState, setModalState] = useState<ModalState>({ step: 'idle' });
  const queryClient = useQueryClient();
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());
  const dialogRef = useRef<HTMLDivElement>(null);
  const isSubmitting = modalState.step === 'submitting';

  function markTouched(field: string) {
    setTouchedFields((prev) => (prev.has(field) ? prev : new Set(prev).add(field)));
  }

  // Reset fields whenever the modal is (re)opened.
  useEffect(() => {
    if (!isOpen) return;
    setTenantNames(applicantName);
    setLandlordName('');
    setLandlordContact('');
    setRequestedInformation('');
    setReferees([emptyReferee()]);
    setModalState({ step: 'idle' });
    setTouchedFields(new Set());
  }, [isOpen, applicantName]);

  // Restore focus to whatever was focused before the modal opened (typically the
  // "Start reference check" trigger) exactly once per open/close cycle. Kept as its
  // own effect (keyed only on `isOpen`) so it isn't re-triggered by modalState
  // transitions the way the focus-trap effect below is.
  useEffect(() => {
    if (!isOpen) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    return () => {
      previouslyFocused?.focus?.();
    };
  }, [isOpen]);

  // Keyboard focus trap + Escape handler
  useEffect(() => {
    if (!isOpen) return;

    const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't allow Escape to unmount the modal mid-submit — the in-flight request's
      // eventual error (if any) would otherwise have nowhere to be shown (FM7).
      if (e.key === 'Escape') { if (modalState.step !== 'submitting') onClose(); return; }
      if (e.key !== 'Tab') return;
      const dialog = dialogRef.current;
      if (!dialog) return;
      const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last?.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first?.focus(); }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    const dialog = dialogRef.current;
    if (dialog) {
      const first = dialog.querySelector<HTMLElement>(FOCUSABLE);
      first?.focus();
    }
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, modalState]);

  function updateReferee(index: number, field: keyof RefereeDraft, value: string) {
    setReferees((prev) => prev.map((r, i) => (i === index ? { ...r, [field]: value } : r)));
  }

  function addReferee() {
    setReferees((prev) => [...prev, emptyReferee()]);
  }

  function removeReferee(index: number) {
    setReferees((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
  }

  const parsedTenantNames = tenantNames
    .split(',')
    .map((n) => n.trim())
    .filter((n) => n.length > 0);

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const refereesValid =
    referees.length > 0 &&
    referees.every((r) => EMAIL_RE.test(r.email.trim()) && r.relationship.trim().length > 0);

  const canSubmit =
    parsedTenantNames.length > 0 &&
    landlordName.trim().length > 0 &&
    landlordContact.trim().length > 0 &&
    requestedInformation.trim().length > 0 &&
    refereesValid;

  async function handleSubmit() {
    if (modalState.step !== 'idle' || !canSubmit) return;
    setModalState({ step: 'submitting' });
    try {
      const body = {
        applicantId,
        tenantNames: parsedTenantNames,
        landlordName: landlordName.trim(),
        landlordContact: landlordContact.trim(),
        requestedInformation: requestedInformation.trim(),
        referees: referees.map<ReferenceCheckRefereeInput>((r) => ({
          email: r.email.trim(),
          relationship: r.relationship.trim(),
          ...(r.context.trim() ? { context: r.context.trim() } : {}),
        })),
      };
      const result = await triggerReferenceCheck(leadId, body);
      onCreated?.(result.referenceCheckId);
      setModalState({ step: 'success', referenceCheckId: result.referenceCheckId });
    } catch (err) {
      const agentOff = err instanceof ApiClientError && err.code === ReferenceCheckConflictCodeSchema.enum.REFERENCE_AGENT_DISABLED;
      if (agentOff) {
        // Refresh the lead (it carries the lead owner's switch) so the trigger and its hint catch up.
        void queryClient.invalidateQueries({ queryKey: ['lead', leadId] });
      }
      setModalState({
        step: 'error',
        agentOff,
        message: agentOff
          ? REFERENCE_AGENT_DISABLED_MESSAGE
          : err instanceof Error
            ? err.message
            : 'Failed to create reference check',
      });
    }
  }

  function handleTryAgain() {
    setModalState({ step: 'idle' });
  }

  if (!isOpen) return null;

  const inputClass = 'w-full border border-input rounded-md px-3 py-2 text-sm bg-background text-foreground outline-none focus:ring-2 focus:ring-ring';
  const errorInputClass = 'w-full border border-destructive rounded-md px-3 py-2 text-sm bg-background text-foreground outline-none focus:ring-2 focus:ring-destructive';
  const labelClass = 'block text-sm font-medium mb-1';
  const errorTextClass = 'text-xs text-destructive mt-1';
  const requiredMark = <span className="text-destructive" aria-hidden="true">*</span>;

  const tenantNamesInvalid = touchedFields.has('tenantNames') && parsedTenantNames.length === 0;
  const landlordNameInvalid = touchedFields.has('landlordName') && landlordName.trim().length === 0;
  const landlordContactInvalid = touchedFields.has('landlordContact') && landlordContact.trim().length === 0;
  const requestedInformationInvalid =
    touchedFields.has('requestedInformation') && requestedInformation.trim().length === 0;

  const content = (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4"
      data-testid="reference-check-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="reference-check-modal-title"
      onClick={(e) => {
        // Don't allow a stray backdrop click to unmount the modal mid-submit — the
        // in-flight request's eventual error (if any) would otherwise have nowhere
        // to be shown (FM7).
        if (isSubmitting) return;
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        className="bg-card rounded-lg border border-border w-full max-w-lg p-6 shadow-lg max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <h2 id="reference-check-modal-title" className="text-lg font-semibold">
            Start Reference Check
          </h2>
          <button
            type="button"
            data-testid="reference-check-close-btn"
            onClick={onClose}
            disabled={isSubmitting}
            className="min-h-[44px] min-w-[44px] flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors ml-4 shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Idle — main form */}
        {modalState.step === 'idle' && (
          <div className="space-y-6">
            <p className="text-sm text-muted-foreground">
              This sends reference requests to the referees below. Referee replies are never
              shown here — only their status.
            </p>

            {/* Information package */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-foreground">Information Package</h3>

              <div>
                <label htmlFor="reference-check-tenant-names" className={labelClass}>
                  Tenant Name(s) {requiredMark}
                </label>
                <input
                  id="reference-check-tenant-names"
                  type="text"
                  data-testid="reference-check-tenant-names-input"
                  value={tenantNames}
                  onChange={(e) => setTenantNames(e.target.value)}
                  onBlur={() => markTouched('tenantNames')}
                  placeholder="e.g. Jane Doe, John Doe"
                  className={tenantNamesInvalid ? errorInputClass : inputClass}
                  required
                  aria-required="true"
                  aria-invalid={tenantNamesInvalid}
                  aria-describedby={tenantNamesInvalid ? 'reference-check-tenant-names-error' : undefined}
                />
                {tenantNamesInvalid ? (
                  <p
                    id="reference-check-tenant-names-error"
                    role="alert"
                    className={errorTextClass}
                    data-testid="reference-check-tenant-names-error"
                  >
                    Enter at least one tenant name.
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground mt-1">Comma-separated if more than one.</p>
                )}
              </div>

              <div>
                <label htmlFor="reference-check-landlord-name" className={labelClass}>
                  Your Name {requiredMark}
                </label>
                <input
                  id="reference-check-landlord-name"
                  type="text"
                  data-testid="reference-check-landlord-name-input"
                  value={landlordName}
                  onChange={(e) => setLandlordName(e.target.value)}
                  onBlur={() => markTouched('landlordName')}
                  placeholder="e.g. Alex Manager"
                  className={landlordNameInvalid ? errorInputClass : inputClass}
                  required
                  aria-required="true"
                  aria-invalid={landlordNameInvalid}
                  aria-describedby={landlordNameInvalid ? 'reference-check-landlord-name-error' : undefined}
                  autoComplete="name"
                />
                {landlordNameInvalid && (
                  <p
                    id="reference-check-landlord-name-error"
                    role="alert"
                    className={errorTextClass}
                    data-testid="reference-check-landlord-name-error"
                  >
                    Enter your name.
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="reference-check-landlord-contact" className={labelClass}>
                  Your Contact Info {requiredMark}
                </label>
                <input
                  id="reference-check-landlord-contact"
                  type="text"
                  data-testid="reference-check-landlord-contact-input"
                  value={landlordContact}
                  onChange={(e) => setLandlordContact(e.target.value)}
                  onBlur={() => markTouched('landlordContact')}
                  placeholder="e.g. alex@example.com or (312) 555-0100"
                  className={landlordContactInvalid ? errorInputClass : inputClass}
                  required
                  aria-required="true"
                  aria-invalid={landlordContactInvalid}
                  aria-describedby={landlordContactInvalid ? 'reference-check-landlord-contact-error' : undefined}
                />
                {landlordContactInvalid && (
                  <p
                    id="reference-check-landlord-contact-error"
                    role="alert"
                    className={errorTextClass}
                    data-testid="reference-check-landlord-contact-error"
                  >
                    Enter your contact info.
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="reference-check-requested-information" className={labelClass}>
                  Requested Information {requiredMark}
                </label>
                <textarea
                  id="reference-check-requested-information"
                  data-testid="reference-check-requested-information-input"
                  value={requestedInformation}
                  onChange={(e) => setRequestedInformation(e.target.value)}
                  onBlur={() => markTouched('requestedInformation')}
                  placeholder="What should the referee confirm? e.g. rental payment history, lease compliance"
                  rows={3}
                  className={requestedInformationInvalid ? errorInputClass : inputClass}
                  required
                  aria-required="true"
                  aria-invalid={requestedInformationInvalid}
                  aria-describedby={requestedInformationInvalid ? 'reference-check-requested-information-error' : undefined}
                />
                {requestedInformationInvalid && (
                  <p
                    id="reference-check-requested-information-error"
                    role="alert"
                    className={errorTextClass}
                    data-testid="reference-check-requested-information-error"
                  >
                    Describe what information you&apos;re requesting.
                  </p>
                )}
              </div>
            </div>

            {/* Referees */}
            <div className="space-y-4 border-t border-border pt-4">
              <h3 className="text-sm font-semibold text-foreground">Referees {requiredMark}</h3>

              <div className="space-y-4">
                {referees.map((referee, index) => {
                  const emailTouched = touchedFields.has(`referee-${index}-email`);
                  const relationshipTouched = touchedFields.has(`referee-${index}-relationship`);
                  const emailTrimmed = referee.email.trim();
                  const emailInvalid = emailTouched && !EMAIL_RE.test(emailTrimmed);
                  const relationshipInvalid = relationshipTouched && referee.relationship.trim().length === 0;

                  return (
                    <div
                      key={index}
                      className="space-y-2 rounded-md border border-border p-3"
                      data-testid={`reference-check-referee-group-${index}`}
                    >
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-medium text-muted-foreground">Referee {index + 1}</p>
                        {referees.length > 1 && (
                          <button
                            type="button"
                            data-testid={`reference-check-referee-remove-${index}`}
                            onClick={() => removeReferee(index)}
                            className="min-h-[44px] min-w-[44px] inline-flex items-center justify-center px-2 text-xs text-destructive hover:text-destructive/80"
                            aria-label={`Remove referee ${index + 1}`}
                          >
                            Remove
                          </button>
                        )}
                      </div>

                      <div>
                        <label htmlFor={`reference-check-referee-email-${index}`} className="sr-only">
                          Referee {index + 1} email
                        </label>
                        <input
                          id={`reference-check-referee-email-${index}`}
                          type="email"
                          data-testid={`reference-check-referee-email-${index}`}
                          value={referee.email}
                          onChange={(e) => updateReferee(index, 'email', e.target.value)}
                          onBlur={() => markTouched(`referee-${index}-email`)}
                          placeholder="Referee email"
                          className={emailInvalid ? errorInputClass : inputClass}
                          required
                          aria-required="true"
                          aria-invalid={emailInvalid}
                          aria-describedby={emailInvalid ? `reference-check-referee-email-error-${index}` : undefined}
                          autoComplete="off"
                        />
                        {emailInvalid && (
                          <p
                            id={`reference-check-referee-email-error-${index}`}
                            role="alert"
                            className={errorTextClass}
                            data-testid={`reference-check-referee-email-error-${index}`}
                          >
                            {emailTrimmed.length === 0
                              ? "Enter the referee's email."
                              : 'Enter a valid email address.'}
                          </p>
                        )}
                      </div>

                      <div>
                        <label htmlFor={`reference-check-referee-relationship-${index}`} className="sr-only">
                          Referee {index + 1} relationship
                        </label>
                        <input
                          id={`reference-check-referee-relationship-${index}`}
                          type="text"
                          data-testid={`reference-check-referee-relationship-${index}`}
                          value={referee.relationship}
                          onChange={(e) => updateReferee(index, 'relationship', e.target.value)}
                          onBlur={() => markTouched(`referee-${index}-relationship`)}
                          placeholder="Relationship, e.g. Previous landlord"
                          className={relationshipInvalid ? errorInputClass : inputClass}
                          required
                          aria-required="true"
                          aria-invalid={relationshipInvalid}
                          aria-describedby={
                            relationshipInvalid ? `reference-check-referee-relationship-error-${index}` : undefined
                          }
                        />
                        {relationshipInvalid && (
                          <p
                            id={`reference-check-referee-relationship-error-${index}`}
                            role="alert"
                            className={errorTextClass}
                            data-testid={`reference-check-referee-relationship-error-${index}`}
                          >
                            Enter the referee&apos;s relationship to the tenant.
                          </p>
                        )}
                      </div>

                      <div>
                        <label htmlFor={`reference-check-referee-context-${index}`} className="sr-only">
                          Referee {index + 1} context (optional)
                        </label>
                        <input
                          id={`reference-check-referee-context-${index}`}
                          type="text"
                          data-testid={`reference-check-referee-context-${index}`}
                          value={referee.context}
                          onChange={(e) => updateReferee(index, 'context', e.target.value)}
                          placeholder="Context (optional)"
                          className={inputClass}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              <Button
                type="button"
                variant="outline"
                size="sm"
                data-testid="reference-check-add-referee-btn"
                onClick={addReferee}
                className="min-h-[44px]"
              >
                Add referee
              </Button>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-2 border-t border-border">
              <Button
                type="button"
                data-testid="reference-check-submit-btn"
                disabled={!canSubmit}
                onClick={() => void handleSubmit()}
                className="flex-1"
              >
                Send Reference Check
              </Button>
              <Button type="button" variant="outline" data-testid="reference-check-cancel-btn" onClick={onClose}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Submitting */}
        {isSubmitting && (
          <div className="flex flex-col items-center justify-center py-8 gap-3">
            <div className="w-8 h-8 border-4 border-border border-t-primary rounded-full animate-spin" />
            <p className="text-sm text-muted-foreground">Creating reference check…</p>
          </div>
        )}

        {/* Success */}
        {modalState.step === 'success' && (
          <div
            data-testid="reference-check-success"
            className="flex flex-col items-center text-center py-4 gap-4"
          >
            <div className="w-14 h-14 rounded-full bg-success/10 flex items-center justify-center">
              <svg className="w-7 h-7 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <h3 className="text-base font-semibold text-foreground mb-1">Reference check started</h3>
              <p className="text-sm text-muted-foreground">
                Requests will go out to the referees. Track status from the Verifications tab.
              </p>
            </div>
            <Button
              type="button"
              data-testid="reference-check-done-btn"
              onClick={onClose}
              className="w-full"
            >
              Done
            </Button>
          </div>
        )}

        {/* Error */}
        {modalState.step === 'error' && (
          <div className="space-y-4">
            <div
              className="p-3 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-sm"
              role="alert"
              data-testid="reference-check-error"
            >
              {modalState.message}
            </div>
            {modalState.agentOff && (
              <p className="text-sm text-muted-foreground" data-testid="reference-check-agent-off-note">
                Turn the reference agent on in Settings → AI agents, then start the reference check again.
                What you typed here is not kept when you leave.
              </p>
            )}
            <div className="flex gap-3">
              {modalState.agentOff ? (
                <Button asChild className="flex-1">
                  <Link to={REFERENCE_AGENT_SETTINGS_PATH} data-testid="reference-check-agent-settings-btn">
                    Open agent settings
                  </Link>
                </Button>
              ) : (
                <Button
                  type="button"
                  data-testid="reference-check-try-again-btn"
                  onClick={handleTryAgain}
                  className="flex-1"
                >
                  Try again
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                data-testid="reference-check-error-cancel-btn"
                onClick={onClose}
              >
                {modalState.agentOff ? 'Close' : 'Cancel'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(content, document.body);
}
